import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from 'react';
import type { InventoryService } from '../application/inventory-service';
import type { HouseholdSharingPort } from '../application/household-sharing';
import type { HouseholdSyncResult } from '../application/household-sharing';
import type { FrozenSaveMonetizationPort } from '../application/monetization';
import type { AppRuntime } from '../config/runtime';
import type { InventoryEvent, InventoryMutation, InventorySnapshot } from '../domain/model';
import { DomainError } from '../domain/errors';
import { useI18n } from '../i18n/I18nContext';
import { eventToastMessage } from '../i18n/event-messages';
import type { AppLocale } from '../i18n/catalog';
import type { MessageKey } from '../i18n/catalog';

interface ToastState {
  id: number;
  message: string;
  tone: 'success' | 'error' | 'info';
  undoEvent: InventoryEvent | null;
}

interface AppContextValue {
  service: InventoryService;
  sharing: HouseholdSharingPort;
  monetization: FrozenSaveMonetizationPort | null;
  runtime: AppRuntime;
  snapshot: InventorySnapshot | null;
  loading: boolean;
  busyKeys: ReadonlySet<string>;
  toast: ToastState | null;
  refresh: () => Promise<InventorySnapshot>;
  synchronizeHousehold: (preferRemote?: boolean) => Promise<HouseholdSyncResult | null>;
  perform: <T>(
    key: string,
    action: () => Promise<T>,
    options?: { success?: string; silent?: boolean },
  ) => Promise<T | null>;
  performMutation: (
    key: string,
    action: () => Promise<InventoryMutation>,
    success?: string,
  ) => Promise<InventoryMutation | null>;
  undo: (event: InventoryEvent) => Promise<void>;
  dismissToast: () => void;
}

const AppContext = createContext<AppContextValue | null>(null);

export const AppProvider = ({
  service,
  sharing,
  monetization,
  runtime,
  children,
}: {
  service: InventoryService;
  sharing: HouseholdSharingPort;
  monetization?: FrozenSaveMonetizationPort;
  runtime: AppRuntime;
  children: ReactNode;
}) => {
  const { locale, t } = useI18n();
  const translateRef = useRef(t);
  const localeRef = useRef<AppLocale>(locale);
  useEffect(() => {
    translateRef.current = t;
    localeRef.current = locale;
  }, [locale, t]);
  const [snapshot, setSnapshot] = useState<InventorySnapshot | null>(null);
  const snapshotRef = useRef<InventorySnapshot | null>(null);
  useEffect(() => {
    snapshotRef.current = snapshot;
  }, [snapshot]);
  const [loading, setLoading] = useState(true);
  const [busyKeys, setBusyKeys] = useState<ReadonlySet<string>>(new Set());
  const [toast, setToast] = useState<ToastState | null>(null);
  const locks = useRef(new Set<string>());
  const toastCounter = useRef(0);
  const initialization = useRef<Promise<InventorySnapshot> | null>(null);
  const householdSyncTail = useRef<Promise<void>>(Promise.resolve());
  const householdSyncRetryTimer = useRef<number | null>(null);
  const householdSyncRetryAttempt = useRef(0);
  const householdSyncNotice = useRef<'conflict' | 'network' | 'revoked' | null>(null);
  const localizedError = useCallback((error: unknown, fallback: MessageKey): string => {
    if (!(error instanceof DomainError)) return translateRef.current(fallback);
    const keys: Record<DomainError['code'], MessageKey> = {
      INVALID_STATE: 'error.invalidState',
      INVALID_QUANTITY: 'error.invalidQuantity',
      UNIT_MISMATCH: 'error.unitMismatch',
      STALE_UNDO: 'error.staleUndo',
      LIMIT_REACHED: 'error.limitReached',
      NOT_FOUND: 'error.notFound',
      DUPLICATE_SERIAL: 'error.duplicateSerial',
      RESTORE_CONFLICT: 'backup.importChanged',
      STALE_CONTAINER: 'error.staleContainer',
    };
    return translateRef.current(keys[error.code]);
  }, []);

  const showToast = useCallback(
    (
      message: string,
      tone: ToastState['tone'] = 'success',
      undoEvent: InventoryEvent | null = null,
    ) => {
      toastCounter.current += 1;
      setToast({ id: toastCounter.current, message, tone, undoEvent });
    },
    [],
  );

  const refresh = useCallback(async (): Promise<InventorySnapshot> => {
    const next = await service.snapshot();
    snapshotRef.current = next;
    setSnapshot(next);
    return next;
  }, [service]);

  const synchronizeHousehold = useCallback((preferRemote = false): Promise<HouseholdSyncResult | null> => {
    const pending = householdSyncTail.current.then(async () => {
      const current = snapshotRef.current;
      if (current?.household == null || !sharing.available) return null;
      const result = await sharing.synchronize(current, preferRemote);
      if (result.state !== 'remote') return result;

      // Une saisie locale arrivée pendant la lecture distante ne doit jamais être écrasée.
      if (!preferRemote && snapshotRef.current !== current) {
        return { state: 'conflict' as const, checkpoint: result.checkpoint };
      }
      await service.restoreBackup(result.snapshot);
      const applied = await refresh();
      await sharing.confirmRemoteApplied(applied, result.checkpoint);
      return result;
    });
    householdSyncTail.current = pending.then(() => undefined, () => undefined);
    return pending;
  }, [refresh, service, sharing]);

  useEffect(() => {
    let active = true;
    initialization.current ??= service
      .initialize()
      .then(() => service.snapshot())
      .then((next) =>
        runtime.mode === 'demo' && next.meta.demoSeededAt == null
          ? service.ensurePublicDemo()
          : next,
      );
    void initialization.current
      .then((next) => {
        if (active) {
          snapshotRef.current = next;
          setSnapshot(next);
        }
      })
      .catch((error: unknown) => {
        if (active) {
          showToast(
            localizedError(error, 'error.storage'),
            'error',
          );
        }
      })
      .finally(() => {
        if (active) {
          setLoading(false);
        }
      });
    return () => {
      active = false;
    };
  }, [localizedError, runtime.mode, service, showToast]);

  useEffect(() => {
    if (runtime.mode !== 'app' || !sharing.available || snapshot?.household == null) return;
    let disposed = false;
    let launchTimer: number | null = null;

    const clearRetry = () => {
      if (householdSyncRetryTimer.current != null) {
        window.clearTimeout(householdSyncRetryTimer.current);
        householdSyncRetryTimer.current = null;
      }
    };
    const scheduleRetry = (run: () => Promise<void>) => {
      clearRetry();
      const delay = Math.min(300_000, 15_000 * 2 ** householdSyncRetryAttempt.current);
      householdSyncRetryAttempt.current = Math.min(householdSyncRetryAttempt.current + 1, 5);
      householdSyncRetryTimer.current = window.setTimeout(() => void run(), delay);
    };
    const run = async () => {
      if (disposed || navigator.onLine === false || document.visibilityState === 'hidden') return;
      try {
        const result = await synchronizeHousehold();
        if (disposed || result == null) return;
        if (result.state === 'conflict') {
          if (householdSyncNotice.current !== 'conflict') {
            householdSyncNotice.current = 'conflict';
            showToast(translateRef.current('sharing.conflict'), 'info');
          }
          return;
        }
        if (result.state === 'revoked') {
          if (householdSyncNotice.current !== 'revoked') {
            householdSyncNotice.current = 'revoked';
            showToast(translateRef.current('sharing.revoked'), 'error');
          }
          return;
        }
        if (result.state === 'awaiting_key') {
          scheduleRetry(run);
          return;
        }
        clearRetry();
        householdSyncRetryAttempt.current = 0;
        householdSyncNotice.current = null;
      } catch {
        if (disposed) return;
        if (householdSyncNotice.current !== 'network') {
          householdSyncNotice.current = 'network';
          showToast(translateRef.current('sharing.errorNetwork'), 'info');
        }
        scheduleRetry(run);
      }
    };
    const launch = () => {
      if (launchTimer != null) window.clearTimeout(launchTimer);
      launchTimer = window.setTimeout(() => void run(), 750);
    };
    const resume = () => {
      clearRetry();
      householdSyncRetryAttempt.current = 0;
      if (document.visibilityState !== 'hidden') void run();
    };

    window.addEventListener('online', resume);
    document.addEventListener('visibilitychange', resume);
    launch();
    return () => {
      disposed = true;
      if (launchTimer != null) window.clearTimeout(launchTimer);
      clearRetry();
      window.removeEventListener('online', resume);
      document.removeEventListener('visibilitychange', resume);
    };
  }, [runtime.mode, sharing, showToast, snapshot, synchronizeHousehold]);

  const perform = useCallback(
    async <T,>(
      key: string,
      action: () => Promise<T>,
      options?: { success?: string; silent?: boolean },
    ): Promise<T | null> => {
      if (locks.current.has(key)) {
        return null;
      }
      locks.current.add(key);
      setBusyKeys(new Set(locks.current));
      try {
        const result = await action();
        await refresh();
        if (!options?.silent && options?.success) {
          showToast(options.success);
        }
        return result;
      } catch (error: unknown) {
        if (error instanceof DomainError && error.code === 'STALE_CONTAINER') {
          try { await refresh(); } catch { /* Preserve the original conflict notice. */ }
        }
        showToast(localizedError(error, 'error.unexpected'), 'error');
        return null;
      } finally {
        locks.current.delete(key);
        setBusyKeys(new Set(locks.current));
      }
    },
    [localizedError, refresh, showToast],
  );

  const performMutation = useCallback(
    async (
      key: string,
      action: () => Promise<InventoryMutation>,
      success?: string,
    ): Promise<InventoryMutation | null> => {
      if (locks.current.has(key)) {
        return null;
      }
      locks.current.add(key);
      setBusyKeys(new Set(locks.current));
      try {
        const mutation = await action();
        await refresh();
        showToast(
          success ?? eventToastMessage(
            mutation.event,
            localeRef.current,
            snapshotRef.current?.meta.demoSeededAt != null,
          ),
          'success',
          mutation.event,
        );
        return mutation;
      } catch (error: unknown) {
        if (error instanceof DomainError && error.code === 'STALE_CONTAINER') {
          try { await refresh(); } catch { /* Preserve the original conflict notice. */ }
        }
        showToast(localizedError(error, 'error.unexpected'), 'error');
        return null;
      } finally {
        locks.current.delete(key);
        setBusyKeys(new Set(locks.current));
      }
    },
    [localizedError, refresh, showToast],
  );

  const undo = useCallback(
    async (event: InventoryEvent): Promise<void> => {
      await performMutation(`undo:${event.id}`, () => service.undo(event));
    },
    [performMutation, service],
  );

  const value = useMemo<AppContextValue>(
    () => ({
      service,
      sharing,
      monetization: monetization ?? null,
      runtime,
      snapshot,
      loading,
      busyKeys,
      toast,
      refresh,
      synchronizeHousehold,
      perform,
      performMutation,
      undo,
      dismissToast: () => setToast(null),
    }),
    [service, sharing, monetization, runtime, snapshot, loading, busyKeys, toast, refresh, synchronizeHousehold, perform, performMutation, undo],
  );

  return <AppContext.Provider value={value}>{children}</AppContext.Provider>;
};

export const useApp = (): AppContextValue => {
  const value = useContext(AppContext);
  if (value == null) {
    throw new Error('AppProvider manquant.');
  }
  return value;
};
